// Shared ZegoCloud call settings for doctor-patient (and supervision) calls.
//
// Cost and privacy choices:
// - Camera always starts OFF; a person turns it on only if they want to.
//   Zego bills a person at the audio rate while they receive no video, so a
//   session that stays camera-off costs about a quarter of a video one.
// - Video quality starts at 360p and can never go above 720p (anything
//   above 720p is billed at the Full HD rate, more than double).
// - No screen sharing (a clinician's screen holds other patients' details),
//   no Zego chat or participant list (our apps have their own), no room
//   details (they show the room link).
// - Two guards against paying for forgotten calls: leave after 10 minutes
//   with nobody else in the room, and a hard stop well after the booking's
//   time (slot + 160 minutes: room for the largest extension a platform admin
//   can allow, 5 x 30). The doctor's call window ends calls on time itself
//   (scheduling/session_limits.py); this only catches a call nobody ended.
const ALONE_LIMIT_MS = 10 * 60 * 1000;

export function zegoCallConfig({ ZegoUIKitPrebuilt, getZp, audioOnly = false, durationMinutes = 30, onLeaveRoom, onRemoved, onOthersChange }) {
  let aloneTimer = null;
  let othersInRoom = 0;
  const hangUp = () => { try { getZp()?.hangUp(); } catch (e) { /* already left */ } };
  const startAloneTimer = () => {
    clearTimeout(aloneTimer);
    aloneTimer = setTimeout(hangUp, ALONE_LIMIT_MS);
  };
  const hardStop = setTimeout(hangUp, ((durationMinutes || 30) + 160) * 60 * 1000);
  const dispose = () => { clearTimeout(aloneTimer); clearTimeout(hardStop); };

  const R = ZegoUIKitPrebuilt;
  const config = {
    scenario: { mode: R.VideoConference },
    maxUsers: 2,
    showPreJoinView: false,
    turnOnMicrophoneWhenJoining: true,
    turnOnCameraWhenJoining: false,
    showMyCameraToggleButton: !audioOnly,
    videoResolutionDefault: R.VideoResolution_360P,
    videoResolutionList: [R.VideoResolution_360P, R.VideoResolution_480P, R.VideoResolution_720P],
    showScreenSharingButton: false,
    showTextChat: false,
    showUserList: false,
    showRoomDetailsButton: false,
    showLayoutButton: false,
    showPinButton: false,
    showRoomTimer: true,
    lowerLeftNotification: { showTextChat: false, showUserJoinAndLeave: true },
    showLeavingView: false,
    // onOthersChange(n): how many other people are in the room (0 = waiting).
    onJoinRoom: () => { startAloneTimer(); if (onOthersChange) onOthersChange(othersInRoom); },
    onUserJoin: (users) => {
      othersInRoom += (users || []).length;
      clearTimeout(aloneTimer);
      if (onOthersChange) onOthersChange(othersInRoom);
    },
    onUserLeave: (users) => {
      othersInRoom = Math.max(0, othersInRoom - (users || []).length);
      if (othersInRoom === 0) startAloneTimer();
      if (onOthersChange) onOthersChange(othersInRoom);
    },
    onLeaveRoom: () => { dispose(); if (onLeaveRoom) onLeaveRoom(); },
    // The room was closed by the server (the doctor ended the session).
    onYouRemovedFromRoom: () => { dispose(); if (onRemoved) onRemoved(); else if (onLeaveRoom) onLeaveRoom(); },
  };
  return { config, dispose };
}
